import React, { useState } from 'react'
import { Modal as ModalBS } from 'react-bootstrap'
import { useToggle } from 'react-use'
import isEmpty from 'lodash/isEmpty'
import { getWindow } from '@ztechapps/common.utils'
import { BusyButton, CopyToClipboardButton } from '@ztechapps/webui.button'
import { TextInput } from '@ztechapps/webui.input'
import { Modal, ModalDragHandle } from '@ztechapps/webui.modal'
import { Alert, HorizontalStack, Link, ShowIf } from '@ztechapps/webui.util'

const PAT_SETTINGS_PATH = '/app/settings/organization/personal-access-token'
const TASK_DETAIL_PATH = '/app/tasks/detail'

const buildFeedUrl = (pat: string, type: 'my-tasks' | 'all-tasks') => {
  const origin = getWindow()?.location?.origin ?? ''
  const taskDetailBaseUrl = `${origin}${TASK_DETAIL_PATH}`
  return `${origin}/api/tasks/ics/${encodeURIComponent(pat)}?type=${type}&baseUrl=${encodeURIComponent(taskDetailBaseUrl)}`
}

export const TaskCalendarSync = () => {
  const [show, toggleShow] = useToggle(false)
  const [pat, setPat] = useState('')
  const [urlsGenerated, setUrlsGenerated] = useState(false)

  const trimmedPat = pat.trim()
  const isPatEmpty = isEmpty(trimmedPat)

  const onOpen = () => {
    setPat('')
    setUrlsGenerated(false)
    toggleShow(true)
  }

  const onClose = () => {
    setPat('')
    setUrlsGenerated(false)
    toggleShow(false)
  }

  const onGenerate = () => {
    setUrlsGenerated(true)
  }

  const myTasksUrl = buildFeedUrl(trimmedPat, 'my-tasks')
  const allTasksUrl = buildFeedUrl(trimmedPat, 'all-tasks')

  return (
    <>
      <BusyButton bsSize="sm" variant="outline-primary" icon="calendar" text="Export iCal" onClick={onOpen} />
      <Modal show={show} onHide={onClose} size="md" backdrop="static" trigger={() => null}>
        {() => (
          <>
            <ModalDragHandle>Export to Calendar</ModalDragHandle>
            <ModalBS.Body>
              <ShowIf condition={!urlsGenerated}>
                <div className="mb-3">
                  <label className="form-label">Personal Access Token</label>
                  <TextInput
                    value={pat}
                    onChange={(e) => setPat(e.target.value)}
                    placeholder="Paste your access token here"
                    autoFocus={false}
                  />
                  <small className="text-secondary mt-1 d-block">
                    Create a token in{' '}
                    <Link href={PAT_SETTINGS_PATH}>Settings &gt; Organization &gt; Personal Access Token</Link>
                  </small>
                </div>
                <BusyButton
                  variant="primary"
                  text="Generate URLs"
                  icon="link"
                  onClick={onGenerate}
                  disabled={isPatEmpty}
                />
              </ShowIf>

              <ShowIf condition={urlsGenerated}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">My Tasks</label>
                  <HorizontalStack gap={2}>
                    <div className="flex-grow-1">
                      <TextInput value={myTasksUrl} readOnly autoFocus={false} />
                    </div>
                    <CopyToClipboardButton textToCopy={myTasksUrl} />
                  </HorizontalStack>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">All Tasks</label>
                  <HorizontalStack gap={2}>
                    <div className="flex-grow-1">
                      <TextInput value={allTasksUrl} readOnly autoFocus={false} />
                    </div>
                    <CopyToClipboardButton textToCopy={allTasksUrl} />
                  </HorizontalStack>
                </div>
                <Alert type="info" icon="circle-info">
                  <span>Calendar apps poll this feed every 6-24 hours. Changes may not appear immediately.</span>
                </Alert>
              </ShowIf>
            </ModalBS.Body>
            <ModalBS.Footer>
              <ShowIf condition={urlsGenerated}>
                <BusyButton
                  variant="outline-primary"
                  text="Back"
                  icon="arrow-left"
                  onClick={() => setUrlsGenerated(false)}
                />
              </ShowIf>
              <BusyButton variant="outline-primary" text="Close" icon="ban" onClick={onClose} />
            </ModalBS.Footer>
          </>
        )}
      </Modal>
    </>
  )
}
